import { Box, Stack, Theme } from '@mui/material';
import { CSSProperties, FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { Scrollbars } from 'react-custom-scrollbars-2';
import { createUseStyles } from 'react-jss';
import Text from '../text';
import ResizeBox from '../resize-box';
import { debounce } from 'lodash';
import ResizeScrollbars from '@/components/resize-scrollbars';

const SCROLLVIEW_PADDING_BOTTOM = 4;

interface Props {
  children: ReactNode[];
  max?: number;
  emptyMessage?: string;
  selected?: string;
}

interface ScrollbarsWithUpdate extends Scrollbars {
  update: () => void;
}

const FLEX_GAP = 2;

const useStyles = createUseStyles<string, { hasScrollbars: boolean }>((theme: Theme) => ({
  scrollBox: {
    width: '100%',
    '& div::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
      '-webkit-box-shadow': 'none',
    },
    '& .scrollbox-child': {
      width: '100%',
    },
  },
  scrollView: {
    display: 'flex',
    flexDirection: 'column',
    gap: FLEX_GAP,
    width: '100%',
    // Room so the last row is not clipped by the custom scrollbar view / subpixel rounding
    paddingBottom: SCROLLVIEW_PADDING_BOTTOM,
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.grey[300]} transparent`,
    paddingRight: ({ hasScrollbars }) => (hasScrollbars ? 8 : 0),
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ffffff35',
      outline: 'none',
      borderRadius: theme.shape.borderRadius,
    },
    '&::-webkit-scrollbar': {
      width: 10,
      backgroundColor: 'transparent',
    },
  },
  scrollHideVert: {
    display: ({ hasScrollbars }) => (hasScrollbars ? 'block' : 'none'),
  },
  scrollThumbVert: {
    backgroundColor: '#00000045',
    borderRadius: theme.shape.borderRadius,
  },
  scrollHideHorizontal: {
    display: 'none',
  },
}));

const ScrollBox: FC<Props> = ({ children, max = 3, emptyMessage = '', selected }) => {
  const needsScroll = children.length > max;
  const styles = useStyles({ hasScrollbars: needsScroll });
  const ref = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<ScrollbarsWithUpdate>(null);
  const [loaded, setLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!needsScroll) {
      return;
    }
    const scrollBox = ref.current;
    if (!scrollBox || !isVisible || loaded) {
      return;
    }

    const visibleCount = Math.min(max, children.length);
    const scrollChildren = scrollBox.querySelectorAll('.scrollbox-child');

    const visibleChildHeight = Array.from(scrollChildren)
      .slice(0, visibleCount)
      .reduce((totalHeight: number, child: HTMLElement, index: number) => {
        return totalHeight + (index ? FLEX_GAP : 0) + Number(child?.offsetHeight || 0);
      }, 0);

    scrollBox.style.height = `${visibleChildHeight + SCROLLVIEW_PADDING_BOTTOM}px`;
    setLoaded(true);
  }, [max, children.length, isVisible, loaded, needsScroll]);

  // Track when the ScrollBox becomes visible
  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([{ isIntersecting, intersectionRatio }]) => {
        setTimeout(() => {
          setIsVisible(isIntersecting && intersectionRatio === 1);
        }, 100);
      },
      { threshold: 1, root: null }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const showEmptyMessage = !children.length && !!emptyMessage;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    debounce(() => {
      scrollbarRef.current?.update();
    }, 50),
    []
  );

  // ensure the selected element is visible when the list loads
  useEffect(() => {
    if (!needsScroll || !selected || !loaded || !isVisible || !ref.current) {
      return;
    }

    const selectedEl = ref.current.querySelector(`[id="${selected}"]`);
    if (!selectedEl) {
      return;
    }

    selectedEl.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  }, [selected, needsScroll, loaded, isVisible, children.length]);

  if (!needsScroll) {
    return (
      <Box className={styles.scrollBox} width="100%">
        {showEmptyMessage && (
          <Stack pt={1}>
            <Text size="small">{emptyMessage}</Text>
          </Stack>
        )}
        <Stack sx={{ gap: `${FLEX_GAP}px` }} width="100%">
          {children.map((child, index) => (
            <Box key={index} className="scrollbox-child" width="100%">
              {child}
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box className={styles.scrollBox} ref={ref}>
      <ResizeScrollbars
        renderView={(props: { style: CSSProperties }) => {
          // completely hide the horizontal scrollbar
          props.style.overflowX = 'hidden';
          props.style.marginBottom = 0;

          // hide the vertical scrollbar when not needed
          delete props.style.overflow;
          props.style.overflowY = 'auto';

          return <div {...props} className={styles.scrollView} />;
        }}
        renderTrackVertical={(props) => <div {...props} className={styles.scrollHideVert}></div>}
        renderThumbVertical={(props) => <div {...props} className={styles.scrollThumbVert}></div>}
        renderTrackHorizontal={(props) => (
          <div {...props} className={styles.scrollHideHorizontal}></div>
        )}
        renderThumbHorizontal={(props) => (
          <div {...props} className={styles.scrollHideHorizontal}></div>
        )}
        autoHide={false}
        ref={scrollbarRef}
      >
        {showEmptyMessage && (
          <Stack pt={1}>
            <Text size="small">{emptyMessage}</Text>
          </Stack>
        )}

        {children.map((child, index) => {
          if (index >= max && !loaded) {
            return null;
          }
          return (
            <ResizeBox key={index} className="scrollbox-child" onResize={handleResize}>
              {child}
            </ResizeBox>
          );
        })}
      </ResizeScrollbars>
    </Box>
  );
};

export default ScrollBox;
